import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { MOCK } from "@/mock";

import vokuMark from "@/assets/voku-mark.jpeg";
import targetDesign from "@/assets/home/target-design.jpeg";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  ArrowDown,
  ArrowUpRight,
  Eye,
  Shield,
  Cable,
  Lock,
  Cpu,
  Zap,
} from "lucide-react";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function AbstractOpticsVisual() {
  // Dark, analog optics feel: no pixels/noise textures.
  return (
    <div className="relative overflow-hidden rounded-none border border-white/10 bg-white/[0.02] p-6 backdrop-blur-xl">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 opacity-[0.14] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:64px_64px]" />
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_30%,rgba(0,230,168,0.18),transparent_60%),radial-gradient(55%_45%_at_20%_70%,rgba(255,255,255,0.08),transparent_60%)]" />
      </div>

      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-white/55">
            Analog optics
          </div>
          <div className="text-xs uppercase tracking-[0.18em] text-white/55">
            no frames
          </div>
        </div>

        <svg
          viewBox="0 0 720 420"
          className="mt-5 h-[320px] w-full"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Abstract light moving through a lens"
        >
          <defs>
            <linearGradient id="beam" x1="0" x2="1">
              <stop offset="0%" stopColor="rgba(0,230,168,0.00)" />
              <stop offset="35%" stopColor="rgba(0,230,168,0.55)" />
              <stop offset="70%" stopColor="rgba(0,230,168,0.20)" />
              <stop offset="100%" stopColor="rgba(0,230,168,0.00)" />
            </linearGradient>
            <radialGradient id="lens" cx="50%" cy="50%" r="55%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.10)" />
              <stop offset="55%" stopColor="rgba(255,255,255,0.06)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.00)" />
            </radialGradient>
            <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feColorMatrix
                in="blur"
                type="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
                result="glow"
              />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Beam */}
          <path
            d="M 70 130 C 210 120, 290 150, 360 210 C 440 280, 520 300, 650 295"
            stroke="url(#beam)"
            strokeWidth="18"
            fill="none"
            filter="url(#softGlow)"
            strokeLinecap="round"
          />

          {/* Lens body */}
          <circle cx="420" cy="220" r="140" fill="url(#lens)" />
          <circle cx="420" cy="220" r="140" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="2" />
          <circle cx="420" cy="220" r="92" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="2" />

          {/* Collimated rays */}
          {Array.from({ length: 7 }).map((_, i) => {
            const y = 145 + i * 22;
            return (
              <path
                key={`ray-${i}`}
                d={`M 40 ${y} C 220 ${y - 8}, 260 ${y + 4}, 360 ${y + 10} C 460 ${y + 18}, 540 ${y + 12}, 690 ${y + 10}`}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
                fill="none"
              />
            );
          })}
        </svg>

        <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4 text-xs text-white/55">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#00E6A8]" />
            <span>Photon path: direct</span>
          </div>
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-white/60" />
            <span>Pipeline: non-digital</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopNav() {
  const nav = useMemo(
    () => [
      { label: "Technology", id: "technology" },
      { label: "IP", id: "ip" },
      { label: "Applications", id: "applications" },
      { label: "Privacy", id: "privacy" },
      { label: "FAQ", id: "faq" },
    ],
    [],
  );

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-[#050505]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-[76px] max-w-6xl items-center justify-between px-5 md:px-8">
        <button
          type="button"
          onClick={() => scrollToId("top")}
          className="group flex items-center gap-3"
        >
          <div className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-none border border-white/15 bg-white/[0.02]">
            <div
              className="absolute -inset-1 -z-10 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background:
                  "radial-gradient(24px 24px at 50% 50%, rgba(0,230,168,0.28), transparent 70%)",
              }}
            />
            <img
              src={vokuMark}
              alt="VOKU mark"
              className="h-full w-full object-cover opacity-95"
            />
          </div>
          <div className="text-sm font-semibold tracking-[0.14em] text-white">
            VOKU TECH
          </div>
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {nav.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => scrollToId(n.id)}
              className="text-sm text-white/55 transition-colors hover:text-white"
            >
              {n.label}
            </button>
          ))}
          <Link
            to="/vision"
            className="text-sm text-white/55 transition-colors hover:text-white"
          >
            Vision
          </Link>
        </nav>

        <div className="hidden md:block">
          <a
            href="mailto:contact@vokutech.com"
            className="text-sm text-white/60 transition-colors hover:text-white"
          >
            Contact
          </a>
        </div>
      </div>
    </header>
  );
}

function SectionHeading({ kicker, title, description }) {
  return (
    <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-white/55">{kicker}</div>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-white md:text-3xl">
          {title}
        </h2>
      </div>
      {description ? (
        <div className="max-w-md text-sm text-white/60">{description}</div>
      ) : null}
    </div>
  );
}

export default function Home() {
  return (
    <div id="top" className="min-h-screen bg-[#050505] text-white">
      <TopNav />

      {/* background */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.10)_1px,transparent_1px)] [background-size:72px_72px]" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_560px_at_60%_10%,rgba(0,230,168,0.10),transparent_60%),radial-gradient(700px_520px_at_12%_70%,rgba(255,255,255,0.05),transparent_60%)]" />
      </div>

      {/* HERO */}
      <section className="pt-[118px] md:pt-[132px]">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid items-start gap-12 md:grid-cols-2 md:gap-16">
            <div>
              <div className="inline-flex flex-wrap items-center gap-2 rounded-none border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-white/70">
                {MOCK.home.hero.badges.map((b) => (
                  <span key={b} className="whitespace-nowrap">
                    {b}
                  </span>
                ))}
              </div>

              <h1 className="mt-5 text-balance text-4xl font-semibold tracking-tight md:text-6xl">
                {MOCK.home.hero.headline}
              </h1>
              <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-white/75 md:text-lg">
                {MOCK.home.hero.subheadline}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  onClick={() => scrollToId("technology")}
                  className="h-12 rounded-none bg-[#00E6A8] px-6 text-black hover:bg-white hover:text-black"
                >
                  Discover the Technology
                  <ArrowDown className="ml-2 h-4 w-4" />
                </Button>

                <div className="text-xs text-white/55">
                  Moving light directly, not data.
                </div>
              </div>
            </div>

            <div className="md:pt-2">
              <div className="relative overflow-hidden rounded-none border border-white/10 bg-white/[0.02] backdrop-blur-xl">
                <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:64px_64px]" />

                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={targetDesign}
                    alt="VOKU target design"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-black/25" />
                </div>

                <div className="relative flex items-center justify-between border-t border-white/10 px-5 py-4">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/60">
                    target design
                  </div>
                  <div className="text-xs text-white/55">abstract optics • analog-direct</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TECHNOLOGY / BOTTLENECK */}
      <section id="technology" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading
            kicker="Technology"
            title="The Digital Bottleneck."
            description="Latency isn’t a UX issue. It’s a physiology issue."
          />

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white">Digital AR</div>
                <Badge className="rounded-none border border-white/15 bg-white/[0.02] text-white/70">
                  20–60ms
                </Badge>
              </div>
              <div className="mt-4 text-sm text-white/70">
                Capture → encode → compute → display.
              </div>
              <div className="mt-6 space-y-3">
                {[
                  "Buffering & frame queues",
                  "Compression artifacts",
                  "Vestibular mismatch → nausea",
                  "Fatigue, out-of-sync perception",
                ].map((t) => (
                  <div
                    key={t}
                    className="rounded-none border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/75"
                  >
                    {t}
                  </div>
                ))}
              </div>
            </Card>

            <Card className="relative overflow-hidden rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="pointer-events-none absolute -inset-10 opacity-35 blur-3xl"
                style={{
                  background:
                    "radial-gradient(520px 320px at 30% 30%, rgba(0,230,168,0.18), transparent 60%)",
                }}
              />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">VOKU Analog-Direct</div>
                  <Badge className="rounded-none border border-white/15 bg-white/[0.02] text-white/70">
                    &lt;1ms
                  </Badge>
                </div>
                <div className="mt-4 text-sm text-white/70">
                  No buffering. No compression. Just photons.
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-none border border-white/10 bg-black/30 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55">
                      <Zap className="h-4 w-4 text-[#00E6A8]" />
                      Latency
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">&lt;1ms</div>
                  </div>
                  <div className="rounded-none border border-white/10 bg-black/30 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55">
                      <Lock className="h-4 w-4 text-white/70" />
                      Buffering
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">None</div>
                  </div>
                </div>

                <div className="mt-6 rounded-none border border-white/10 bg-black/30 p-4 text-sm text-white/70">
                  Moving light directly, not data.
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* IP */}
      <section id="ip" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading
            kicker="Trust"
            title="Protected by a Hardware IP Portfolio."
            description="Defensibility is engineered into the platform." 
          />

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {MOCK.home.ip.map((p) => (
              <Card
                key={p.title}
                className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl"
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="text-sm font-semibold text-white">{p.title}</div>
                    <div className="mt-2 text-sm text-white/70">{p.desc}</div>
                  </div>
                  <div className="rounded-none border border-white/10 bg-black/30 px-3 py-2 text-xs text-white/70">
                    {p.patent}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section id="applications" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading
            kicker="Applications"
            title="Built for the edge cases."
            description="Where <1ms matters most. Three verticals. One constraint: reality must stay real-time."
          />

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {/* Vision */}
            <Card className="group relative overflow-hidden rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-none border border-white/15 bg-black/30">
                    <Eye className="h-5 w-5 text-white/80" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/55">Active</div>
                    <div className="mt-1 text-lg font-semibold text-white">VOKU Vision</div>
                  </div>
                </div>
                <Badge className="rounded-none border border-white/15 bg-white/[0.02] text-white/70">
                  Medical
                </Badge>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                Restoring peripheral vision for Macular Degeneration patients. Real-time clarity.
              </p>
              <div className="mt-6">
                <Button
                  asChild
                  className="h-11 w-full rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black"
                >
                  <Link to="/vision">
                    Explore VOKU Vision
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </Card>

            {/* Link */}
            <Card className="group relative overflow-hidden rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-none border border-white/15 bg-black/30">
                    <Cable className="h-5 w-5 text-white/80" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/55">Active</div>
                    <div className="mt-1 text-lg font-semibold text-white">VOKU Link</div>
                  </div>
                </div>
                <Badge className="rounded-none border border-white/15 bg-white/[0.02] text-white/70">
                  Enterprise
                </Badge>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                Zero-latency, hardware-secured telepresence channel. 100% offline.
              </p>
              <div className="mt-6">
                <a href="https://www.vokulink.com" target="_blank" rel="noreferrer">
                  <Button className="h-11 w-full rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black">
                    Go to VOKU Link
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </Card>

            {/* Ops */}
            <Card className="relative overflow-hidden rounded-none border-white/10 bg-white/[0.02] p-6 text-white/90 backdrop-blur-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-none border border-white/10 bg-black/20">
                    <Shield className="h-5 w-5 text-white/60" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/45">Inactive</div>
                    <div className="mt-1 text-lg font-semibold text-white/85">VOKU Ops</div>
                  </div>
                </div>
                <Badge className="rounded-none border border-white/10 bg-white/[0.01] text-white/55">
                  R&amp;D Phase
                </Badge>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/60 md:text-base">
                Unjammable, offline rear-view awareness for special operations.
              </p>
              <div className="mt-6 flex items-center justify-between gap-3">
                <div className="text-xs text-white/45">R&D track. Limited disclosures.</div>

                <Button
                  asChild
                  variant="outline"
                  className="h-10 rounded-none border-white/15 bg-transparent px-4 text-white/80 hover:bg-white/10 hover:text-white"
                >
                  <Link to="/ops">
                    View Ops Briefing
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* PRIVACY */}
      <section id="privacy" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading
            kicker="Privacy"
            title="Hardware-Native Privacy."
            description="100% Offline. No Cloud. No Data Logging. What happens between devices stays between devices."
          />

          <div className="mt-8 grid items-start gap-10 md:grid-cols-2 md:gap-14">
            <div>
              <div className="mt-1 space-y-3">
                {MOCK.home.privacy.features.map((f) => (
                  <div
                    key={f}
                    className="flex items-start gap-3 rounded-none border border-white/10 bg-white/[0.02] px-4 py-3"
                  >
                    <div className="mt-1 h-2 w-2 rounded-full bg-[#00E6A8]" />
                    <div className="text-sm text-white/80">{f}</div>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-5 text-white backdrop-blur-xl">
                  <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                    Capture
                  </div>
                  <div className="mt-2 text-lg font-semibold text-white">
                    Disabled
                  </div>
                </Card>
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-5 text-white backdrop-blur-xl">
                  <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                    Telemetry
                  </div>
                  <div className="mt-2 text-lg font-semibold text-white">
                    None
                  </div>
                </Card>
              </div>
            </div>

            <Card className="relative overflow-hidden rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:44px_44px]" />
              <div className="relative">
                <div className="flex items-start justify-between gap-8">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                      Physical control
                    </div>
                    <div className="mt-1 text-lg font-semibold text-white">Privacy Switch</div>
                  </div>
                  <div className="rounded-none border border-white/15 bg-black/30 px-3 py-2 text-xs text-white/70">
                    OFFLINE
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="rounded-none border border-white/10 bg-black/30 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/80">Capture</span>
                      <span className="text-sm font-medium text-[#00E6A8]">DISABLED</span>
                    </div>
                    <div className="mt-4 h-2 w-full rounded-none bg-white/10">
                      <div className="h-2 w-[18%] rounded-none bg-[#00E6A8]/70" />
                    </div>
                  </div>
                  <div className="rounded-none border border-white/10 bg-black/30 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/80">Telemetry</span>
                      <span className="text-sm font-medium text-white/85">NONE</span>
                    </div>
                    <div className="mt-4 h-2 w-full rounded-none bg-white/10">
                      <div className="h-2 w-[8%] rounded-none bg-white/50" />
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-none border border-white/10 bg-black/25 p-4">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                        Onboard storage
                      </div>
                      <div className="mt-2 font-mono text-lg text-white">
                        STORAGE: VOLATILE ONLY
                      </div>
                      <div className="mt-1 text-xs text-white/55">
                        0 KB persistent. Clears on power loss.
                      </div>
                    </div>

                    {/* Minimalist local illustration (no external images) */}
                    <div className="relative h-[74px] w-[140px] shrink-0 overflow-hidden rounded-none border border-white/10 bg-black/40">
                      <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.10)_1px,transparent_1px)] [background-size:22px_22px]" />
                      <svg
                        viewBox="0 0 140 74"
                        className="relative h-full w-full"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-label="Volatile memory indicator"
                      >
                        <rect x="38" y="16" width="64" height="42" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.14)" />
                        {Array.from({ length: 8 }).map((_, i) => (
                          <rect
                            key={`pin-top-${i}`}
                            x={42 + i * 8}
                            y="10"
                            width="4"
                            height="6"
                            fill="rgba(255,255,255,0.10)"
                          />
                        ))}
                        {Array.from({ length: 8 }).map((_, i) => (
                          <rect
                            key={`pin-btm-${i}`}
                            x={42 + i * 8}
                            y="58"
                            width="4"
                            height="6"
                            fill="rgba(255,255,255,0.10)"
                          />
                        ))}
                        <path
                          d="M 18 42 L 36 42"
                          stroke="rgba(0,230,168,0.55)"
                          strokeWidth="2"
                        />
                        <path
                          d="M 104 42 L 122 42"
                          stroke="rgba(0,230,168,0.55)"
                          strokeWidth="2"
                        />
                        <path
                          d="M 54 36 L 86 36"
                          stroke="rgba(0,230,168,0.35)"
                          strokeWidth="2"
                        />
                        <path
                          d="M 54 42 L 86 42"
                          stroke="rgba(0,230,168,0.65)"
                          strokeWidth="2"
                        />
                        <path
                          d="M 54 48 L 86 48"
                          stroke="rgba(0,230,168,0.35)"
                          strokeWidth="2"
                        />
                        <path
                          d="M 30 28 L 44 20"
                          stroke="rgba(255,255,255,0.14)"
                          strokeWidth="2"
                        />
                        <path
                          d="M 96 54 L 110 46"
                          stroke="rgba(255,255,255,0.14)"
                          strokeWidth="2"
                        />
                      </svg>
                      <div className="absolute bottom-2 right-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
                        RAM
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading
            kicker="FAQ"
            title="Physics-first, by design."
            description="Minimal claims. Measurable constraints."
          />

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <Accordion type="single" collapsible className="w-full">
                {MOCK.home.faq.map((item, idx) => (
                  <AccordionItem
                    key={item.q}
                    value={`item-${idx}`}
                    className="border-white/10"
                  >
                    <AccordionTrigger className="text-left text-white hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/70">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>

            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">Contact</div>
              <div className="mt-2 text-lg font-semibold text-white">contact@vokutech.com</div>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                For evaluation requests, partnerships, and technical diligence.
              </p>
              <div className="mt-6">
                <a href="mailto:contact@vokutech.com">
                  <Button className="h-11 w-full rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black">
                    Contact
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="mt-20 border-t border-white/10 pb-10 pt-10 md:mt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-white/70">{MOCK.home.footer.left}</div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
              <button type="button" onClick={() => scrollToId("privacy")} className="hover:text-white">
                Privacy
              </button>
              <button type="button" onClick={() => scrollToId("faq")} className="hover:text-white">
                FAQ
              </button>
              <a href="mailto:contact@vokutech.com" className="hover:text-white">
                Contact
              </a>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6 text-xs text-white/45">
            <div className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-white/40" />
              <span>© {new Date().getFullYear()} VOKU</span>
            </div>
            <button
              type="button"
              onClick={() => scrollToId("top")}
              className="text-white/55 transition-colors hover:text-white"
            >
              Back to top
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
